// TimelineMonthView.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import {
    buildTopHeaderSegments,
    DAY_MS,
    formatTimelineRangeText,
    getInclusiveEndOffsetMs,
    getScheduledBounds,
    getScheduledRows,
    getTimelineBarStyle,
    getUnscheduledLabel,
    resolveViewportTimelineBounds,
} from '../shared/timelineViewUtils';

// CSS
import './TimelineMonthView.css';

const MONTH_COLUMN_WIDTH = 96;
const MIN_TRACK_WIDTH = 720;
const MIN_VISIBLE_MONTHS = Math.ceil(MIN_TRACK_WIDTH / MONTH_COLUMN_WIDTH);

const startOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const startOfMonth = (date) => {
    const next = startOfDay(date);
    next.setDate(1);
    return next;
};

const endOfMonth = (date) => {
    const nextMonthStart = startOfMonth(addMonths(date, 1));
    return new Date(nextMonthStart.getTime() - 1);
};

/**
 * TimelineMonthView
 *
 * Right-side timeline renderer for month-scale timeline.
 *
 * Top row: Years
 * Bottom row: Months
 *
 * @returns {ReactNode} The rendered component.
 */
const TimelineMonthView = React.memo(({
    timelineRows = [],
    timelineFieldDef,
    viewportBounds = null,
    onNodeClick,
    onNodeDoubleClick,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    // ----- STATE ----- //

    const [loadedTrailingMonths, setLoadedTrailingMonths] = useState(0);
    // ----- REFS ----- //

    const scrollRef = useRef(null);
    const lastLoadTriggerWidthRef = useRef(0);

    // ----- MEMOS ----- //

    const scheduledRows = useMemo(() => getScheduledRows(timelineRows), [timelineRows]);

    const scheduledSpanResetToken = useMemo(() => {
        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return `none_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
        return `${scheduledBounds.minStart.getTime()}_${scheduledBounds.maxEnd.getTime()}_${timelineFieldDef?.key || timelineFieldDef?.name || ''}`;
    }, [scheduledRows, timelineFieldDef?.key, timelineFieldDef?.name]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setLoadedTrailingMonths(0);
        lastLoadTriggerWidthRef.current = 0;
    }, [scheduledSpanResetToken]);

    // ----- MEMOS ----- //

    const bounds = useMemo(() => {
        const viewport = resolveViewportTimelineBounds(viewportBounds);
        if (viewport) return viewport;

        const scheduledBounds = getScheduledBounds(scheduledRows);
        if (!scheduledBounds) return null;

        const startMonth = startOfMonth(scheduledBounds.minStart);
        const dataEndMonth = startOfMonth(scheduledBounds.maxEnd);
        const minSpanEndMonth = startOfMonth(addMonths(startMonth, MIN_VISIBLE_MONTHS - 1));
        const baseEndMonth = dataEndMonth.getTime() < minSpanEndMonth.getTime()
            ? minSpanEndMonth
            : dataEndMonth;
        const endMonth = loadedTrailingMonths > 0
            ? startOfMonth(addMonths(baseEndMonth, loadedTrailingMonths))
            : baseEndMonth;

        const start = startMonth;
        const end = endOfMonth(endMonth);
        return { start, end };
    }, [scheduledRows, loadedTrailingMonths, viewportBounds]);

    // ----- HANDLERS ----- //

    const handleScroll = useCallback(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const nearRightEdge = scrollEl.scrollLeft + scrollEl.clientWidth >= scrollEl.scrollWidth - 48;
        if (!nearRightEdge) return;

        if (lastLoadTriggerWidthRef.current === scrollEl.scrollWidth) return;
        lastLoadTriggerWidthRef.current = scrollEl.scrollWidth;
        setLoadedTrailingMonths((prev) => prev + 1);
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => handleScroll();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [handleScroll]);

    // ----- MEMOS ----- //

    const columns = useMemo(() => {
        if (!bounds) return [];

        const out = [];
        let cursor = startOfMonth(bounds.start);

        while (cursor.getTime() <= bounds.end.getTime()) {
            const monthStart = new Date(cursor);
            const next = startOfMonth(addMonths(cursor, 1));
            const monthEnd = new Date(next.getTime() - 1);
            out.push({
                key: `m_${monthStart.getTime()}`,
                start: monthStart,
                end: monthEnd,
                topLabel: monthStart.toLocaleDateString('en-US', { year: 'numeric' }),
                bottomLabel: monthStart.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
            });
            cursor = next;
        }

        return out;
    }, [bounds]);

    const topHeaderSegments = useMemo(
        () => buildTopHeaderSegments(columns, { labelKey: 'topLabel', keyPrefix: 'y' }),
        [columns]
    );

    const trackWidth = useMemo(() => (
        Math.max(MIN_TRACK_WIDTH, (columns.length || 1) * MONTH_COLUMN_WIDTH)
    ), [columns.length]);

    const spanMs = useMemo(() => {
        if (!bounds) return 0;
        return Math.max(1, bounds.end.getTime() - bounds.start.getTime());
    }, [bounds]);

    if (!bounds || columns.length === 0) {

        return (
            <div className="timeline-month-view-container">

                {/* EMPTY */}
                <div
                  className="timeline-month-view-empty"
                >
                    No scheduled timeline items yet.
                </div>
            </div>
        );
    }
    // ----- RENDER ----- //

    return (
        <div
          className="timeline-month-view-container"
          style={{
                '--timeline-month-column-width': `${MONTH_COLUMN_WIDTH}px`,
            }}
        >

            {/* SCROLL */}
            <div className="timeline-month-view-scroll" ref={scrollRef}>

                {/* TIMELINE MONTH HEADER */}
                <div
                  className="timeline-month-header"
                  style={{
                        borderBottom: `1px solid ${theme.status}`,
                        width: `${trackWidth}px`,
                    }}
                >

                    {/* TIMELINE MONTH HEADER ROW TOP */}
                    <div
                      className="timeline-month-header-row timeline-month-header-row-top"
                      style={{
                        borderBottom: `1px solid ${theme.status}`,
                      }}
                    >
                        {topHeaderSegments.map((segment) => (
                            <div
                              className="timeline-month-header-cell timeline-month-header-cell-top"
                              style={{
                                width: `${segment.span * MONTH_COLUMN_WIDTH}px`,
                              }}
                              key={segment.key}
                              title={segment.label}
                            >
                                {segment.label}
                            </div>
                        ))}
                    </div>

                    {/* TIMELINE MONTH HEADER ROW BOTTOM */}
                    <div
                      className="timeline-month-header-row timeline-month-header-row-bottom"
                      style={{
                            backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${MONTH_COLUMN_WIDTH}px)`,
                        }}
                    >
                        {columns.map((column) => (
                            <div
                              className="timeline-month-header-cell timeline-month-header-cell-bottom"
                              style={{
                                width: `${MONTH_COLUMN_WIDTH}px`,
                              }}
                              key={column.key}
                              title={column.bottomLabel}
                            >
                                {column.bottomLabel}
                            </div>
                        ))}
                    </div>
                </div>

                {/* TIMELINE MONTH BODY */}
                <div
                  className="timeline-month-body"
                  style={{
                    width: `${trackWidth}px`,
                  }}
                >
                    {timelineRows.map((row, rowIndex) => {
                        const rowDividerStyle = rowIndex > 0
                            ? { borderTop: `1px solid ${theme.status}` }
                            : undefined;

                        if (row.type === 'group-header') {
                            return (
                                <div
                                  className="timeline-month-row timeline-month-row-group"
                                  style={{
                                    ...rowDividerStyle,
                                  }}
                                  key={row.key}
                                />
                            );
                        }

                        const entry = row.entry;
                        const isUnscheduled = row.type === 'item-unscheduled';
                        const barStyle = getTimelineBarStyle({
                            entry,
                            bounds,
                            spanMs,
                            trackWidth,
                            minDurationMs: DAY_MS,
                            minWidthPercent: 1.1,
                            inclusiveEndMs: getInclusiveEndOffsetMs(timelineFieldDef?.type),
                        });
                        const rangeText = formatTimelineRangeText(entry?.range, timelineFieldDef?.type);
                        const showBarLabel = barStyle?.widthPx >= 120;

                        return (
                            <div
                              className="timeline-month-row timeline-month-row-item"
                              style={{
                                    ...rowDividerStyle,
                                    backgroundImage: `repeating-linear-gradient(to right, ${theme.status} 0, ${theme.status} 1px, transparent 1px, transparent ${MONTH_COLUMN_WIDTH}px)`,
                                }}
                              key={row.key}
                              onClick={(event) => onNodeClick?.(event, entry?.node)}
                              onDoubleClick={(event) => onNodeDoubleClick?.(event, entry?.node)}
                            >

                                {/* TIMELINE MONTH UNSCHEDULED */}
                                {isUnscheduled && (
                                    <div
                                      className="timeline-month-unscheduled"
                                    >
                                        {getUnscheduledLabel(timelineFieldDef)}
                                    </div>
                                )}

                                {!isUnscheduled && barStyle && (
                                    <div
                                      className={`timeline-month-bar${entry?.range?.isMilestone ? ' is-milestone' : ''}`}
                                      style={{
                                            backgroundColor: entry?.barColor || theme.highlight,
                                            color: entry?.barForeground || undefined,
                                            left: barStyle.left,
                                            width: barStyle.width,
                                        }}
                                      title={rangeText}
                                    >

                                        {/* TIMELINE MONTH BAR LABEL */}
                                        {showBarLabel && (
                                            <span
                                              className="timeline-month-bar-label"
                                            >{rangeText}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

// Dev Tools
TimelineMonthView.displayName = 'TimelineMonthView';

export default TimelineMonthView;
